import type { BlockEventDefinition, BlockPropDefinition, BlockSlotDefinition } from '../../types/block';
import type { FormSchema, FormSchemaResolved, ResolvableFormSchema } from '../../types/form-schema';
import type { BlockConfigEventDefinition } from './api/config';
import { getBlockConfigFromComponent } from './api/config';

const internalVueProps = new Set([
	'key',
	'ref',
	'ref_for',
	'ref_key',
	'class',
	'style',
]);

const warnedDiagnostics = new WeakMap<object, Set<string>>();

type RuntimeProps = string[] | Record<string, RuntimePropDefinition | undefined>;
type RuntimeEmits = string[] | Record<string, unknown>;

interface RuntimeComponentMeta {
	props?: RuntimeProps;
	__props?: RuntimeProps;
	emits?: RuntimeEmits;
	__emits?: RuntimeEmits;
	__file?: string;
	name?: string;
}

interface RuntimePropDefinition {
	required?: boolean;
	type?: unknown;
}

interface BlockOptionEntry {
	name: string;
	required: boolean;
	type?: BlockPropDefinition['type'];
	array: boolean;
	values?: Array<string | number | boolean | null | undefined>;
	description?: string | null;
	icon?: string | null;
	model: boolean;
}

/**
 * Extract block metadata from explicit `defineBlock()` contract.
 *
 * Block editor metadata comes only from `defineBlock({ options, events })`.
 * Vue runtime props/emits are used only to enrich or validate that contract.
 */
export function extractBlockMeta(comp: unknown): {
	props: BlockPropDefinition[];
	events: BlockEventDefinition[];
	slots: BlockSlotDefinition[];
} {
	if (!isObjectLike(comp)) {
		return { props: [], events: [], slots: [] };
	}

	const config = getBlockConfigFromComponent(comp);
	const runtimeProps = getRuntimePropMap(comp);
	const optionEntries = collectBlockOptionEntries(config.options);
	const optionNames = new Set(optionEntries.map((entry) => entry.name));
	const props = optionEntries.map((entry) => createBlockPropDefinition(entry, runtimeProps.get(entry.name)));
	const propByName = new Map(props.map((prop) => [prop.name, prop] as const));
	const explicitEvents = normalizeExplicitEvents(config.events);
	const runtimeEmits = getRuntimeEmitNames(comp);

	warnForUndeclaredRuntimeProps(comp, runtimeProps, optionNames);
	warnForMissingModelRuntimeProps(comp, optionEntries, runtimeProps);
	warnForExplicitEventMismatches(comp, explicitEvents, runtimeEmits);

	const explicitEventNames = new Set(explicitEvents.map((event) => event.name));
	const synthesizedEvents = optionEntries.flatMap((entry) => {
		if (!entry.model) {
			return [];
		}

		const eventName = `update:${entry.name}`;

		if (explicitEventNames.has(eventName)) {
			warnComponent(
				comp,
				`event-collision:${eventName}`,
				`[Block metadata] ${getComponentLabel(comp)} explicit event "${eventName}" collides with synthesized model event.`,
			);

			return [];
		}

		const prop = propByName.get(entry.name);

		if (!prop) {
			return [];
		}

		return [{
			name: eventName,
			payload: [{
				name: 'value',
				required: prop.required,
				type: prop.type,
				array: prop.array,
				...(prop.values ? { values: prop.values } : {}),
			}],
		} satisfies BlockEventDefinition];
	});

	return {
		props,
		events: [
			...explicitEvents,
			...synthesizedEvents,
		],
		// Slots are resolved at render time from `defineBlock({ slots })` because
		// getter-style slot definitions depend on the component's props.
		slots: [],
	};
}

/**
 * Resolve a FormSchema and flatten named option entries, including groups.
 */
function collectBlockOptionEntries<TResult extends object, TContext>(
	formSchema: FormSchema<TResult, TContext> | undefined,
): BlockOptionEntry[] {
	if (!formSchema) {
		return [];
	}

	const result: BlockOptionEntry[] = [];

	visitOptionEntries(resolveFormSchemaEntries(formSchema), result);

	return result;
}

/**
 * Safely resolve a FormSchema to its current entry array.
 */
function resolveFormSchemaEntries<TResult extends object, TContext>(
	formSchema: FormSchema<TResult, TContext>,
): FormSchemaResolved<TResult> {
	if (Array.isArray(formSchema)) {
		return formSchema;
	}

	try {
		const dummyValues = new Proxy<Record<string, unknown>>({}, {
			get: () => {},
		});
		type ResolvableFormResult = ReturnType<ResolvableFormSchema<TResult, TContext>>;

		return formSchema({
			form: (schema) => schema as ResolvableFormResult,
			values: dummyValues as TResult,
			context: dummyValues as TContext,
		});
	}
	catch {
		return [];
	}
}

/**
 * Visit named option entries recursively.
 */
function visitOptionEntries<TResult extends object>(
	entries: FormSchemaResolved<TResult>,
	result: BlockOptionEntry[],
) {
	for (const entry of entries) {
		if (!entry) {
			continue;
		}

		if ('type' in entry && entry.type === 'group' && 'fields' in entry) {
			visitOptionEntries(entry.fields, result);
			continue;
		}

		if (!('name' in entry) || !entry.name) {
			continue;
		}

		const entryType = 'type' in entry && isBlockPropType(entry.type) ? entry.type : undefined;

		result.push({
			name: entry.name as string,
			required: entry.required ?? false,
			type: entryType,
			array: entry.array ?? false,
			...entry.values ? { values: entry.values } : {},
			description: entry.description ?? null,
			icon: entry.icon ?? null,
			model: entry.model ?? false,
		});
	}
}

/**
 * Build a block prop definition from explicit option entry plus matching runtime prop.
 */
function createBlockPropDefinition(
	entry: BlockOptionEntry,
	runtimeProp: RuntimePropDefinition | undefined,
): BlockPropDefinition {
	return {
		name: entry.name,
		required: runtimeProp?.required ?? entry.required,
		type: entry.type ?? resolveVueRuntimeType(runtimeProp),
		array: entry.array || runtimeProp?.type === Array,
		...(entry.values ? { values: entry.values } : {}),
		...(entry.description ? { description: entry.description } : {}),
		...(entry.icon ? { icon: entry.icon } : {}),
	};
}

/**
 * Normalize explicit editor event definitions into runtime block event metadata.
 */
function normalizeExplicitEvents(events: BlockConfigEventDefinition[] | undefined): BlockEventDefinition[] {
	return (events ?? []).map((event) => ({
		name: event.name,
		payload: (event.payload ?? []).map((prop) => ({
			name: prop.name,
			required: prop.required ?? false,
			type: prop.type ?? 'object',
			array: prop.array ?? false,
			...(prop.values ? { values: prop.values } : {}),
			...(prop.description ? { description: prop.description } : {}),
			...(prop.icon ? { icon: prop.icon } : {}),
			...(prop.rawType ? { rawType: prop.rawType } : {}),
		})),
		...(event.description ? { description: event.description } : {}),
		...(event.icon ? { icon: event.icon } : {}),
		...(event.rawType ? { rawType: event.rawType } : {}),
		...(event.sort != null ? { sort: event.sort } : {}),
	}));
}

function warnForUndeclaredRuntimeProps(
	comp: object,
	runtimeProps: Map<string, RuntimePropDefinition | undefined>,
	declaredOptionNames: Set<string>,
) {
	const undeclared = [...runtimeProps.keys()].filter((name) =>
		!declaredOptionNames.has(name) && !internalVueProps.has(name),
	);

	if (undeclared.length === 0) {
		return;
	}

	warnComponent(
		comp,
		`undeclared-runtime-props:${undeclared.join(',')}`,
		`[Block metadata] ${getComponentLabel(comp)} runtime props are not declared in defineBlock({ options }): ${undeclared.join(', ')}.`,
	);
}

function warnForMissingModelRuntimeProps(
	comp: object,
	options: BlockOptionEntry[],
	runtimeProps: Map<string, RuntimePropDefinition | undefined>,
) {
	for (const entry of options) {
		if (!entry.model || runtimeProps.has(entry.name)) {
			continue;
		}

		warnComponent(
			comp,
			`missing-model-runtime-prop:${entry.name}`,
			`[Block metadata] ${getComponentLabel(comp)} option "${entry.name}" uses model: true but no matching runtime prop exists.`,
		);
	}
}

function warnForExplicitEventMismatches(
	comp: object,
	events: BlockEventDefinition[],
	runtimeEmitNames: Set<string> | null,
) {
	if (!runtimeEmitNames) {
		return;
	}

	for (const event of events) {
		if (runtimeEmitNames.has(event.name)) {
			continue;
		}

		warnComponent(
			comp,
			`missing-runtime-event:${event.name}`,
			`[Block metadata] ${getComponentLabel(comp)} explicit event "${event.name}" is not declared in runtime emits.`,
		);
	}
}

function getRuntimePropMap(comp: RuntimeComponentMeta): Map<string, RuntimePropDefinition | undefined> {
	const runtimeProps = comp.props ?? comp.__props;

	if (!runtimeProps) {
		return new Map();
	}

	if (Array.isArray(runtimeProps)) {
		return new Map(runtimeProps.map((name) => [name, undefined]));
	}

	return new Map(Object.entries(runtimeProps));
}

function getRuntimeEmitNames(comp: RuntimeComponentMeta): Set<string> | null {
	const runtimeEmits = comp.emits ?? comp.__emits;

	if (!runtimeEmits) {
		return null;
	}

	if (Array.isArray(runtimeEmits)) {
		return new Set(runtimeEmits);
	}

	return new Set(Object.keys(runtimeEmits));
}

function warnComponent(comp: unknown, key: string, message: string) {
	if (import.meta.env.PROD || !isObjectLike(comp)) {
		return;
	}

	const warned = warnedDiagnostics.get(comp) ?? new Set<string>();

	if (warned.has(key)) {
		return;
	}

	warned.add(key);
	warnedDiagnostics.set(comp, warned);
	console.warn(message);
}

function getComponentLabel(comp: unknown): string {
	if (!isObjectLike(comp)) {
		return 'anonymous block component';
	}

	return comp.__file ?? comp.name ?? 'anonymous block component';
}

function isBlockPropType(type: unknown): type is BlockPropDefinition['type'] {
	return type === 'string'
		|| type === 'number'
		|| type === 'boolean'
		|| type === 'enum'
		|| type === 'object';
}

function resolveVueRuntimeType(def: unknown): BlockPropDefinition['type'] {
	if (!def) {
		return 'object';
	}

	const typeValue = isRecord(def) && 'type' in def ? def.type : def;

	if (typeValue === String) {
		return 'string';
	}

	if (typeValue === Number) {
		return 'number';
	}

	if (typeValue === Boolean) {
		return 'boolean';
	}

	if (Array.isArray(typeValue)) {
		return typeValue.length === 1 ? resolveVueRuntimeType(typeValue[0]) : 'object';
	}

	return 'object';
}

/**
 * Check whether a value can safely be used as a WeakMap key and metadata bag.
 */
function isObjectLike(value: unknown): value is RuntimeComponentMeta & object {
	return (typeof value === 'object' && value !== null) || typeof value === 'function';
}

/**
 * Check for plain object-style runtime prop definitions.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}
